import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { ChevronDown, MoreHorizontal, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { SIDEBAR_MODULES } from "@/components/layout/ProjectNavFrame";
import { TopUser } from "@/components/layout/TopUser";
import { cn } from "@/lib/utils";
import {
  NAV_GROUPS, PROJECT_DPR_PAGES, PROJECT_NAV_GROUPS,
  applyRoleFilter, applyPermissionFilter,
  activeTopNavId, topNavPanels, findActivePath, TOP_NAV,
} from "@/components/layout/navConfig";

/* rows shown inside a dropdown for a node with children (what used to be the sidebar tree) */
function rowsFor(node, projectId, depth = 0, out = []) {
  if (node.isProjectSection) {
    const pages = (node.dynamicChildren || PROJECT_DPR_PAGES).filter((p) => !node.allowedPages || node.allowedPages.includes(p));
    PROJECT_NAV_GROUPS.forEach((g) => {
      const ps = pages.filter((p) => g.suffixes.includes(p.suffix));
      if (!ps.length) return;
      out.push({ kind: "head", key: `${node.id}.${g.id}`, label: g.label, depth });
      ps.forEach((p) => out.push({ kind: "link", key: p.testId, to: `/projects/${projectId}/${p.suffix}`, end: p.end, label: p.label, testId: p.testId, depth }));
    });
    return out;
  }
  if (node.children?.length) {
    out.push({ kind: "head", key: node.id, label: node.label, depth });
    node.children.forEach((c) => rowsFor(c, projectId, depth + 1, out));
    return out;
  }
  out.push({ kind: "link", key: node.id, to: node.to, end: node.end, label: node.label, testId: node.testId, depth });
  return out;
}

const tabCls = (active, open) =>
  cn("flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[10px] px-4 text-sm font-semibold transition-colors",
    active ? "bg-[#99002B] text-white" : open ? "bg-white/10 text-white" : "text-[#E6EBF5] hover:bg-white/10 hover:text-white");

/** Logo block at the left of the bar; as wide as the sidebar underneath so the two line up. */
function TopBrand({ collapsed }) {
  return (
    <div data-testid="brand-mark" className={cn("flex h-full shrink-0 items-center gap-2.5", collapsed ? "md:w-16 md:justify-center" : "md:w-[230px] md:px-4")}>
      <img src="/brand/buildnexa-icon.png" alt="buildnexa.io" className="h-9 w-9 shrink-0 rounded-lg object-contain" />
      <div className={cn("min-w-0", collapsed && "md:hidden")}>
        <div className="text-[15px] font-bold leading-none tracking-tight text-white">buildnexa.io</div>
        <div className="mt-1 text-[9.5px] font-semibold uppercase tracking-[0.16em] text-[#9AA7C2]">ERP Platform</div>
      </div>
    </div>
  );
}

const MORE_W = 96; // px reserved for the "More" button
const GAP = 2;

/** Horizontal bar: the selected module's pages. Tabs that do not fit move into "More". */
export function TopNav({ onMobileMenuOpen, collapsed = false }) {
  const { projectId } = useParams();
  const location = useLocation();
  const { isOrgAdmin, seesWholePortfolio } = useAuth();
  const { canView } = usePermissions();

  const [openKey, setOpenKey] = useState(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [visibleCount, setVisibleCount] = useState(Infinity);
  const navRef = useRef(null);
  const measureRef = useRef([]);

  const visibleGroups = useMemo(
    () => applyPermissionFilter(applyRoleFilter(NAV_GROUPS, { seesWholePortfolio, isOrgAdmin }), canView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seesWholePortfolio, isOrgAdmin, canView]
  );
  const activeTopId = activeTopNavId(visibleGroups, location.pathname, projectId);
  const contextual = useMemo(
    () => (activeTopId ? topNavPanels(visibleGroups, activeTopId) : visibleGroups.filter((g) => g.id !== "administration")),
    [visibleGroups, activeTopId]
  );
  const items = useMemo(
    () => contextual.flatMap((g) => g.items).filter((n) => !(n.isProjectSection && !projectId))
      .filter((n) => !(SIDEBAR_MODULES.includes(activeTopId) && !n.isProjectSection)),
    [contextual, projectId]
  );
  const activeChain = useMemo(() => new Set(findActivePath(visibleGroups, location.pathname, projectId)), [visibleGroups, location.pathname, projectId]);
  const hasKids = (n) => Boolean(n.isProjectSection || n.children?.length);
  const isActive = (n) =>
    activeChain.has(n.id) ||
    (!hasKids(n) && n.to && (n.to === "/" ? location.pathname === "/" : location.pathname === n.to || location.pathname.startsWith(`${n.to}/`)));

  /* which tabs fit: measure every tab in an invisible fixed row */
  const measure = useCallback(() => {
    const box = navRef.current;
    if (!box) return;
    const widths = measureRef.current.slice(0, items.length).map((el) => (el ? el.getBoundingClientRect().width + GAP : 0));
    const total = widths.reduce((x, y) => x + y, 0);
    if (total <= box.clientWidth) { setVisibleCount(Infinity); return; }
    let used = 0, n = 0;
    for (const w of widths) { if (used + w + MORE_W > box.clientWidth) break; used += w; n++; }
    setVisibleCount(Math.max(1, n));
  }, [items.length]);
  useLayoutEffect(() => {
    if (!navRef.current) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(navRef.current);
    const t = setTimeout(measure, 0);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, [measure, items]);

  useEffect(() => { setOpenKey(null); }, [location.pathname]);
  useEffect(() => {
    const onDown = (e) => {
      if (e.target.closest?.("[data-topnav-portal],[data-topnav-btn]")) return;
      setOpenKey(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const openDrop = (key, el) => {
    if (openKey === key) { setOpenKey(null); return; }
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - 260)) });
    setOpenKey(key);
  };

  const vc = visibleCount === Infinity ? items.length : visibleCount;
  const shown = items.slice(0, vc);
  const overflow = items.slice(vc);
  const moreActive = overflow.some(isActive);
  const openNode = openKey && openKey !== "__more" ? items.find((i) => i.id === openKey) : null;
  const dropRows = openKey === "__more"
    ? overflow.flatMap((n) => rowsFor(n, projectId))
    : openNode ? rowsFor(openNode, projectId) : [];

  return (
    <>
      {/* invisible measuring row (outside any overflow clip) */}
      {createPortal(
        <div aria-hidden="true" className="pointer-events-none invisible fixed left-0 top-0 flex items-center gap-0.5">
          {items.map((n, i) => (
            <div key={n.id} ref={(el) => (measureRef.current[i] = el)} className="shrink-0">
              <span className={tabCls(false, false)}>{n.label}{hasKids(n) && <ChevronDown className="h-3 w-3" />}</span>
            </div>
          ))}
        </div>,
        document.body
      )}

      <header data-testid="app-topbar" className="sticky top-0 z-40 flex h-[60px] shrink-0 items-center gap-3 bg-[#18233C] pr-4 text-white shadow-md">
        <button data-testid="mobile-menu-button" onClick={onMobileMenuOpen} aria-label="Open navigation" className="ml-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#C5CFE6] hover:bg-white/10 hover:text-white md:hidden">
          <Menu className="h-5 w-5" />
        </button>
        <TopBrand collapsed={collapsed} />

        <nav ref={navRef} data-testid="topnav-pages" className="flex min-w-0 flex-1 items-center justify-start gap-0.5 overflow-hidden">
          {shown.map((node) =>
            !hasKids(node) ? (
              <NavLink key={node.id} to={node.to} end={node.end ?? node.to === "/"} data-testid={node.testId}
                className={({ isActive: a }) => tabCls(a, false)}>
                {node.label}
              </NavLink>
            ) : (
              <button key={node.id} type="button" data-topnav-btn data-testid={`sidebar-module-toggle-${node.slug || node.id}`}
                onClick={(e) => openDrop(node.id, e.currentTarget)} className={tabCls(isActive(node), openKey === node.id)}>
                {node.label}
                <ChevronDown className={cn("h-3 w-3 transition-transform", openKey === node.id && "rotate-180")} />
              </button>
            )
          )}
          {items.length === 0 && activeTopId && (
            <span className="flex h-10 items-center rounded-[10px] bg-[#99002B] px-4 text-sm font-semibold text-white">{TOP_NAV.find((t) => t.id === activeTopId)?.label}</span>
          )}
          {overflow.length > 0 && (
            <button type="button" data-topnav-btn data-testid="topbar-more-button"
              onClick={(e) => openDrop("__more", e.currentTarget)} className={cn(tabCls(moreActive, openKey === "__more"), "ml-0.5")}>
              <MoreHorizontal className="h-4 w-4" /> More
              <ChevronDown className={cn("h-3 w-3 transition-transform", openKey === "__more" && "rotate-180")} />
            </button>
          )}
        </nav>

        <TopUser moduleId={projectId ? "projects" : activeTopId} />
      </header>

      {dropRows.length > 0 && createPortal(
        <div data-topnav-portal style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 9999 }}
          className="max-h-[75vh] min-w-[220px] overflow-y-auto rounded-xl border border-white/10 bg-[#1F2B47] py-1 shadow-2xl">
          {dropRows.map((r) =>
            r.kind === "head" ? (
              <p key={r.key} style={{ paddingLeft: 16 + r.depth * 10 }} className="px-4 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-widest text-[#6B7A8D]">{r.label}</p>
            ) : (
              <NavLink key={r.key} to={r.to} end={r.end ?? r.to === "/"} data-testid={r.testId} onClick={() => setOpenKey(null)}
                style={{ paddingLeft: 16 + r.depth * 10 }}
                className={({ isActive: a }) => cn("block py-2 pr-4 text-[13px] font-medium transition-colors whitespace-nowrap", a ? "bg-[#99002B] text-white" : "text-[#E6EBF5]/85 hover:bg-white/10 hover:text-white")}>
                {r.label}
              </NavLink>
            )
          )}
        </div>,
        document.body
      )}
    </>
  );
}
